<template>
  <div class="card mb-3">
    <div
      class="card-header bg-light d-flex justify-content-between align-items-center"
      role="button"
      data-testid="picker-deal-header"
      @click="open = !open"
    >
      <h5 class="mb-0">
        {{ $t("forwards.picker.deal.title") }}
        <span v-if="form.name" class="fw-normal text-muted ms-2">
          · {{ form.name }}
        </span>
      </h5>
      <FontAwesomeIcon :icon="open ? 'chevron-up' : 'chevron-down'" />
    </div>

    <div v-show="open" class="card-body">
      <div class="row g-3">
        <div class="col-12 col-lg-6">
          <label class="form-label" :for="nameId">
            {{ $t("forwards.picker.criteria.name") }}
          </label>
          <input
            :id="nameId"
            v-model="name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': showNameError }"
            :maxlength="NAME_MAX"
            :placeholder="$t('forwards.picker.criteria.name_placeholder')"
            data-testid="picker-deal-name"
          />
          <div class="invalid-feedback">
            {{ $t("forwards.picker.criteria.name_required") }}
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <label class="form-label" :for="descriptionId">
            {{ $t("forwards.picker.criteria.description_label") }}
          </label>
          <input
            :id="descriptionId"
            v-model="descriptionText"
            type="text"
            class="form-control"
            :placeholder="
              $t('forwards.picker.criteria.description_placeholder')
            "
            :aria-describedby="descriptionHelpId"
          />
          <div :id="descriptionHelpId" class="form-text">
            {{ $t("forwards.common.deal_text.description_help") }}
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <label class="form-label" :for="internalNotesId">
            {{ $t("forwards.common.deal_text.internal_notes") }}
          </label>
          <textarea
            :id="internalNotesId"
            v-model="internalNotes"
            class="form-control"
            rows="2"
            :maxlength="INTERNAL_NOTES_MAX"
            :aria-describedby="internalNotesHelpId"
            data-testid="picker-deal-internal-notes"
          ></textarea>
          <div :id="internalNotesHelpId" class="form-text">
            {{ $t("forwards.common.deal_text.internal_notes_help") }}
          </div>
        </div>
      </div>
      <p class="text-body-tertiary fs-10 mb-0 mt-2">
        {{ $t("forwards.picker.deal.description") }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type DealIdentityForm,
  INTERNAL_NOTES_MAX,
  NAME_MAX,
} from "@/forwards2/picker/pickerForm";
import { computed, ref, useId } from "vue";

/**
 * The deal's own identity, in the slot the spot flow gives "Buyer Details & Contracts".
 *
 * The name, the description and the internal notes: a forward deal's buyer, prices and contract
 * links are entered on the deal page after the draft exists (plan Appendix D.1 row 1d), so there is
 * nothing else to fold in here. Foldable like its spot counterpart, with the name echoed in the
 * header so a folded card still says which deal is being cut.
 */
withDefaults(
  defineProps<{
    /** Marks the name field once everything else is ready and the name is what is left. */
    showNameError?: boolean;
  }>(),
  { showNameError: false }
);

const form = defineModel<DealIdentityForm>({ required: true });

const nameId = useId();
const descriptionId = useId();
const descriptionHelpId = useId();
const internalNotesId = useId();
const internalNotesHelpId = useId();

const open = ref(true);

const name = computed<string>({
  get: () => form.value.name,
  set: (value) => {
    form.value = { ...form.value, name: value };
  },
});

const descriptionText = computed<string>({
  get: () => form.value.description,
  set: (value) => {
    form.value = { ...form.value, description: value };
  },
});

const internalNotes = computed<string>({
  get: () => form.value.internal_notes,
  set: (value) => {
    form.value = { ...form.value, internal_notes: value };
  },
});
</script>
